import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Truck, Clock, ShieldAlert, Banknote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface MonitoringMetrics {
  fezFailedDeliveries: number;
  stuckProcessing: number;
  stuckAwaitingConfirmation: number;
  failedEscrowReleases: number;
}

export function PlatformMonitoring() {
  const [metrics, setMetrics] = useState<MonitoringMetrics>({
    fezFailedDeliveries: 0,
    stuckProcessing: 0,
    stuckAwaitingConfirmation: 0,
    failedEscrowReleases: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const fortyEightHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000).toISOString();

      const [fezFailed, stuckProcessing, stuckConfirmation, failedEscrow] = await Promise.all([
        // Fez failed deliveries in last 24h
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('delivery_type', 'fez')
          .eq('delivery_status', 'failed')
          .gte('updated_at', twentyFourHoursAgo),

        // Orders stuck in 'paid' status > 48h (not yet processed by seller)
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'paid')
          .lt('created_at', fortyEightHoursAgo),

        // Orders awaiting buyer confirmation > 48h
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'awaiting_buyer_confirmation')
          .lt('updated_at', fortyEightHoursAgo),

        // Failed escrow releases (escrow transactions with status 'failed')
        supabase
          .from('escrow_transactions')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'failed')
          .gte('created_at', twentyFourHoursAgo),
      ]);

      setMetrics({
        fezFailedDeliveries: fezFailed.count || 0,
        stuckProcessing: stuckProcessing.count || 0,
        stuckAwaitingConfirmation: stuckConfirmation.count || 0,
        failedEscrowReleases: failedEscrow.count || 0,
      });
    } catch (error) {
      console.error('Error fetching monitoring metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const totalAlerts = metrics.fezFailedDeliveries + metrics.stuckProcessing + metrics.stuckAwaitingConfirmation + metrics.failedEscrowReleases;

  const metricCards = [
    {
      label: "Fez Failed Deliveries (24h)",
      value: metrics.fezFailedDeliveries,
      icon: Truck,
      color: metrics.fezFailedDeliveries > 0 ? "text-red-600" : "text-green-600",
      bgColor: metrics.fezFailedDeliveries > 0 ? "border-red-500/20 bg-red-500/5" : "border-green-500/20 bg-green-500/5",
    },
    {
      label: "Orders Stuck Processing (>48h)",
      value: metrics.stuckProcessing,
      icon: Clock,
      color: metrics.stuckProcessing > 0 ? "text-yellow-600" : "text-green-600",
      bgColor: metrics.stuckProcessing > 0 ? "border-yellow-500/20 bg-yellow-500/5" : "border-green-500/20 bg-green-500/5",
    },
    {
      label: "Awaiting Confirmation (>48h)",
      value: metrics.stuckAwaitingConfirmation,
      icon: ShieldAlert,
      color: metrics.stuckAwaitingConfirmation > 0 ? "text-orange-600" : "text-green-600",
      bgColor: metrics.stuckAwaitingConfirmation > 0 ? "border-orange-500/20 bg-orange-500/5" : "border-green-500/20 bg-green-500/5",
    },
    {
      label: "Failed Escrow Releases (24h)",
      value: metrics.failedEscrowReleases,
      icon: Banknote,
      color: metrics.failedEscrowReleases > 0 ? "text-red-600" : "text-green-600",
      bgColor: metrics.failedEscrowReleases > 0 ? "border-red-500/20 bg-red-500/5" : "border-green-500/20 bg-green-500/5",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-lg">Platform Monitoring</CardTitle>
              <CardDescription>Real-time system health & alerts</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {totalAlerts === 0 ? (
              <Badge variant="default" className="bg-green-600">All Clear</Badge>
            ) : (
              <Badge variant="destructive">{totalAlerts} Alert{totalAlerts > 1 ? 's' : ''}</Badge>
            )}
            <Button variant="outline" size="sm" onClick={fetchMetrics} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metricCards.map((metric) => (
            <Card key={metric.label} className={metric.bgColor}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <metric.icon className={`h-4 w-4 ${metric.color}`} />
                  <span className="text-xs font-medium text-muted-foreground">{metric.label}</span>
                </div>
                <div className={`text-2xl font-bold ${metric.color}`}>
                  {loading ? '...' : metric.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
